import { useState } from 'react';
import { useBrand } from '@/config/brands';
import { type HospitalOutlier } from '@/hooks/useFraudCategoryPricing';
import { FraudRiskBadge } from './FraudRiskBadge';
import { markupBadgeColor } from '@/lib/grades';
import { ArrowUpDown, Search as SearchIcon, Star } from 'lucide-react';

// Patriotic palette
const NAVY = '#002868';
const GOLD = '#C5A55A';
const RED = '#B22234';
const CREAM = '#FAF9F6';

interface HospitalOutlierTableProps {
  outliers: HospitalOutlier[];
  onInvestigate: (outlier: HospitalOutlier) => void;
}

type SortKey = 'name' | 'codeCount' | 'avgMarkup' | 'maxMarkup';

const fmtPct = (v: number) => {
  const sign = v >= 0 ? '+' : '';
  if (Math.abs(v) >= 1000) return `${sign}${(v / 1000).toFixed(1)}k%`;
  return `${sign}${v.toFixed(1)}%`;
};

export const HospitalOutlierTable = ({
  outliers,
  onInvestigate,
}: HospitalOutlierTableProps) => {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';

  const [sortKey, setSortKey] = useState<SortKey>('avgMarkup');
  const [sortAsc, setSortAsc] = useState(false);
  const [search, setSearch] = useState('');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const filtered = outliers.filter((o) =>
    search === '' ||
    o.hospitalName.toLowerCase().includes(search.toLowerCase()) ||
    o.city.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case 'name': cmp = a.hospitalName.localeCompare(b.hospitalName); break;
      case 'codeCount': cmp = a.codeCount - b.codeCount; break;
      case 'avgMarkup': cmp = a.avgMarkup - b.avgMarkup; break;
      case 'maxMarkup': cmp = a.maxMarkup - b.maxMarkup; break;
    }
    return sortAsc ? cmp : -cmp;
  });

  const SortHeader = ({ label, field, align = 'left' }: { label: string; field: SortKey; align?: string }) => (
    <th
      className={`p-2.5 font-semibold text-xs tracking-wide uppercase cursor-pointer select-none ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={() => handleSort(field)}
      style={isAhp ? { color: CREAM } : undefined}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className="w-3 h-3 opacity-40" />
      </span>
    </th>
  );

  return (
    <div>
      {/* Search bar */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <SearchIcon
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: isAhp ? 'rgba(27,27,27,0.3)' : undefined }}
          />
          <input
            type="text"
            placeholder="Search hospitals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg outline-none transition-colors"
            style={isAhp ? {
              backgroundColor: 'rgba(255,255,255,0.9)',
              border: `1px solid rgba(0,40,104,0.15)`,
              color: '#1B1B1B',
            } : {
              backgroundColor: '#1a1a2e',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#f0f0f0',
            }}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              style={isAhp ? {
                background: `linear-gradient(135deg, ${NAVY} 0%, #001a4a 100%)`,
              } : undefined}
              className={!isAhp ? 'border-b bg-muted/50' : ''}
            >
              <SortHeader label="Hospital" field="name" />
              <th
                className="text-left p-2.5 font-semibold text-xs tracking-wide uppercase"
                style={isAhp ? { color: CREAM } : undefined}
              >
                City
              </th>
              <SortHeader label="# Codes" field="codeCount" />
              <SortHeader label="Avg Markup" field="avgMarkup" align="right" />
              <SortHeader label="Max Markup" field="maxMarkup" align="right" />
              <th
                className="text-center p-2.5 font-semibold text-xs tracking-wide uppercase"
                style={isAhp ? { color: CREAM } : undefined}
              >
                Risk
              </th>
              <th
                className="text-center p-2.5 font-semibold text-xs tracking-wide uppercase"
                style={isAhp ? { color: CREAM } : undefined}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="text-center py-8"
                  style={{ color: isAhp ? 'rgba(27,27,27,0.35)' : undefined }}
                >
                  {search ? 'No hospitals match your search' : 'No hospital outliers found for this category'}
                </td>
              </tr>
            ) : (
              sorted.map((outlier, idx) => (
                <tr
                  key={outlier.hospitalId}
                  className="cursor-pointer transition-colors"
                  onClick={() => onInvestigate(outlier)}
                  style={isAhp ? {
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(0,40,104,0.02)',
                  } : {
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <td
                    className="p-2.5 text-xs font-medium max-w-[200px] truncate"
                    title={outlier.hospitalName}
                    style={isAhp ? { color: '#1B1B1B' } : undefined}
                  >
                    {outlier.hospitalName}
                  </td>
                  <td
                    className="p-2.5 text-xs"
                    style={{ color: isAhp ? 'rgba(27,27,27,0.62)' : undefined }}
                  >
                    {outlier.city}
                  </td>
                  <td
                    className="p-2.5 text-center font-mono text-xs"
                    style={isAhp ? { color: NAVY, fontWeight: 600 } : undefined}
                  >
                    {outlier.codeCount}
                  </td>
                  <td className="p-2.5 text-right font-mono text-xs font-bold">
                    <span style={{ color: markupBadgeColor(outlier.avgMarkup) }}>
                      {fmtPct(outlier.avgMarkup)}
                    </span>
                  </td>
                  <td className="p-2.5 text-right font-mono text-xs font-bold">
                    <span style={{ color: markupBadgeColor(outlier.maxMarkup) }}>
                      {fmtPct(outlier.maxMarkup)}
                    </span>
                  </td>
                  <td className="p-2.5 text-center">
                    <FraudRiskBadge level={outlier.riskLevel} />
                  </td>
                  <td className="p-2.5 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onInvestigate(outlier);
                      }}
                      className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-all"
                      style={isAhp ? {
                        background: `linear-gradient(135deg, ${NAVY} 0%, #001a4a 100%)`,
                        color: CREAM,
                        border: `1px solid ${GOLD}40`,
                        boxShadow: '0 1px 3px rgba(0,40,104,0.2)',
                      } : {
                        backgroundColor: 'rgba(0,255,65,0.15)',
                        color: '#00ff41',
                        border: '1px solid rgba(0,255,65,0.3)',
                      }}
                    >
                      Investigate
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {sorted.length > 0 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {isAhp && <Star className="w-3 h-3" style={{ color: GOLD, fill: GOLD }} />}
          <p
            className="text-xs"
            style={{ color: isAhp ? 'rgba(27,27,27,0.35)' : undefined }}
          >
            Showing {sorted.length} of {outliers.length} hospitals with pricing data
          </p>
          {isAhp && <Star className="w-3 h-3" style={{ color: GOLD, fill: GOLD }} />}
        </div>
      )}
    </div>
  );
};
